import { flow } from 'lodash';
import parsePhoneNumberFromString from 'libphonenumber-js';

const isStartsWithPlus = (phoneNumber: string) => phoneNumber.startsWith('+');
const isEmergencyNumber = (phoneNumber: string) => phoneNumber.length === 3;

export const trimToTenDigits = (phoneNumber: string) => {
  let number = phoneNumber;
  let prefix = '';

  // Retain the '+' sign if it exists
  if (phoneNumber.startsWith('+')) {
    prefix = '+';
    number = phoneNumber.slice(1);
  }

  // Check if the phone number starts with the USA country code
  if (number.startsWith('1')) {
    const localNumber = number.slice(1);

    // Ensure the local number is exactly 10 digits
    if (localNumber.length > 10) {
      return prefix + number.slice(0, 11); // Include country code and 10 digits of the number
    }
    return prefix + number; // If already 10 digits or less, include the country code
  }

  // If it's not starting with '1', it's not a North American number, return unchanged
  return phoneNumber;
};

export function normalizePhoneNumber(phoneNumber: string) {
  // Remove non-digit characters
  return phoneNumber.replaceAll(/\D/g, '');
}

const DIAL_OUT_PREFIX = '011';
const NANP_COUNTRY_CODE = '1';
const NANP_NUMBER_WITH_COUNTRY_CODE_LENGTH = 11;

/**
 * Reduces a phone number to a form that is stable across the shapes the app
 * stores it in (`+`, NANP country code, 10 or 11 digits, `011` dial prefix).
 * Values without digits (for example `anonymous`) fall back to the trimmed,
 * lowercase value.
 */
export const getComparablePhoneNumber = (phoneNumber: string): string => {
  let digits = normalizePhoneNumber(phoneNumber);

  if (digits.startsWith(DIAL_OUT_PREFIX)) {
    digits = digits.slice(DIAL_OUT_PREFIX.length);
  }

  if (
    digits.length === NANP_NUMBER_WITH_COUNTRY_CODE_LENGTH &&
    digits.startsWith(NANP_COUNTRY_CODE)
  ) {
    digits = digits.slice(NANP_COUNTRY_CODE.length);
  }

  return digits || phoneNumber.trim().toLowerCase();
};

export const isSamePhoneNumber = (first: string, second: string): boolean => {
  const comparableFirst = getComparablePhoneNumber(first);

  return (
    comparableFirst !== '' &&
    comparableFirst === getComparablePhoneNumber(second)
  );
};

export const removePlusFromPhoneNumber = (phoneNumber: string) =>
  phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;

const replaceLettersInPhoneNumber = (phoneNumber: string) =>
  phoneNumber
    .replaceAll(/[A-Ca-c]/g, '2')
    .replaceAll(/[D-Fd-f]/g, '3')
    .replaceAll(/[G-Ig-i]/g, '4')
    .replaceAll(/[J-Lj-l]/g, '5')
    .replaceAll(/[M-Om-o]/g, '6')
    .replaceAll(/[P-Sp-s]/g, '7')
    .replaceAll(/[T-Vt-v]/g, '8')
    .replaceAll(/[W-Zw-z]/g, '9');

export const addNANPCodeIfNeeded = (phoneNumber: string) => {
  const num = phoneNumber.trim();

  // If it starts with '+', return as-is
  if (num.startsWith('+')) {
    return num;
  }

  // If it already starts with '1' and is a valid NANP number, return as-is
  if (num.startsWith('1') && isNANPNumber(num)) {
    return num;
  }

  // If it's a 10-digit number that would be valid with '1' prefix, add '1'
  if (num.length === 10 && isNANPNumber(`1${num}`)) {
    return `1${num}`;
  }

  return num;
};

const remove011Prefix = (phoneNumber: string): string => {
  if (phoneNumber.startsWith('011')) {
    return phoneNumber.substring(3);
  }
  if (phoneNumber.startsWith('+011')) {
    return `+${phoneNumber.substring(4)}`;
  }
  return phoneNumber;
};

export const removeExtNumbers = (phoneNumber: string): string => {
  const [phoneNumberWithoutExt] = phoneNumber.split(/(\s?x\s?\d+)|ext\./gi);
  return phoneNumberWithoutExt.trim();
};

export const addPlusToPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) {
    throw Error('No phone number given');
  }

  return isStartsWithPlus(phoneNumber) || isEmergencyNumber(phoneNumber)
    ? phoneNumber
    : `+${phoneNumber}`;
};

export const parsePhoneNumber = flow([
  removeExtNumbers,
  replaceLettersInPhoneNumber,
  remove011Prefix,
  addNANPCodeIfNeeded,
  trimToTenDigits,
]);

export const parsePhoneNumberWithRemovePlus = flow([
  parsePhoneNumber,
  removePlusFromPhoneNumber,
]);

export const removeAnyLeadingNonNumbers = (s: string) => {
  return s.replaceAll(/^\D+/g, '');
};

export const validateNanpPhoneNumber = (phoneNumber: string): string => {
  // Regular expression to check if the phone number contains only digits, hyphens, periods, or spaces
  const numericOnlyRegex = /^[+0-9-\s.]+$/;

  // If the number contains only numeric characters (and valid separators), return it as-is
  if (numericOnlyRegex.test(phoneNumber)) {
    return phoneNumber;
  }

  // Regular expression to match only the first 3 digits (valid toll-free and premium prefixes)
  const areaCodeRegex = /^1?[-.\s]?(800|833|844|855|866|877|888|900)/;

  // Extract the first 3 digits (area code)
  const areaCodeMatch = phoneNumber.match(areaCodeRegex);
  if (!areaCodeMatch) {
    return '';
  }

  // Get the part of the phone number after the area code
  const restOfNumber = phoneNumber.slice(areaCodeMatch[0].length);

  // Remove any spaces, periods, or hyphens for consistency
  const cleanedNumber = restOfNumber.replaceAll(/[-.\s]/g, '');

  // Check if the remaining part has any invalid characters (anything other than letters, digits, or '+')
  if (/[^a-zA-Z0-9+]/.test(cleanedNumber)) {
    return ''; // Invalid characters, return empty string
  }
  // Validate that the cleaned number has exactly 7 characters (central office + subscriber)
  const nanpRemainingRegex = /^[a-zA-Z0-9]{7}$/; // Matches any 7 valid alphanumeric characters

  // If the rest of the number is valid, return the original phone number
  return nanpRemainingRegex.test(cleanedNumber) ? phoneNumber : '';
};

/**
 * Checks if a phone number belongs to the North American Numbering Plan (NANP).
 * Relies on libphonenumber-js metadata to handle all regions and edge-cases.
 *
 * @param phoneNumber - any string, e.g. "+1 202-555-0125", "12025550125", "2025550125"
 * @returns true if it's a valid NANP number (country calling code 1), false otherwise
 */
export const isNANPNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber) {
    return false;
  }

  const pn = parsePhoneNumberFromString(phoneNumber, 'US');
  return Boolean(pn && pn.isValid() && pn.countryCallingCode === '1');
};

export const parsePhoneNumberIncomingCall = flow([
  validateNanpPhoneNumber,
  parsePhoneNumber,
]);

/**
 * Checks if a number is US toll-free (800/8XX) or premium-rate (900).
 *
 * @param raw - any phone string, e.g. "+1 (800) 555-1212", "9001234567", "18005551212"
 * @returns true for valid US TOLL_FREE or PREMIUM_RATE numbers
 */
export const isUSFreeOrPremiumNumber = (raw: string): boolean => {
  const pn = parsePhoneNumberFromString(raw, 'US');
  if (!pn || !pn.isValid() || pn.country !== 'US') {
    return false;
  }

  // getType() → 'TOLL_FREE', 'PREMIUM_RATE', 'FIXED_LINE', etc.
  const type = pn.getType?.();
  return type === 'TOLL_FREE' || type === 'PREMIUM_RATE';
};

/**
 * Tries to parse a number as international and determine if it should be treated as such
 */
const parseAsInternational = (digits: string): boolean => {
  try {
    const pn = parsePhoneNumberFromString(`+${digits}`);

    if (!pn?.countryCallingCode || pn.countryCallingCode === '1') {
      return false;
    }

    // Special handling for US toll-free numbers that conflict with international codes
    if (isUSFreeOrPremiumNumber(digits)) {
      return false;
    }

    // Consider it international if it's valid OR has a recognized non-US country
    return (
      Boolean(pn.isPossible()) || Boolean(pn.country && pn.country !== 'US')
    );
  } catch {
    return false;
  }
};

/**
 * Tries to parse a number as NANP and determine if it's valid
 */
const parseAsNANP = (digits: string): boolean => {
  try {
    const pn = parsePhoneNumberFromString(digits, 'US');
    if (!pn) {
      return false;
    }
    return Boolean(pn.isPossible()) && pn.countryCallingCode === '1';
  } catch {
    return false;
  }
};

/**
 * Checks if two phone numbers differ only by the "011" prefix
 */
export const doPhoneNumbersDifferOnlyBy011 = (
  phoneNumber1: string,
  phoneNumber2: string
): boolean => {
  const normalized1 = normalizePhoneNumber(phoneNumber1);
  const normalized2 = normalizePhoneNumber(phoneNumber2);

  // If they're already the same, they don't differ by 011
  if (normalized1 === normalized2) {
    return false;
  }

  // Check if one has 011 prefix and the other doesn't, but otherwise match
  const digits1 = phoneNumber1.replaceAll(/\D/g, '');
  const digits2 = phoneNumber2.replaceAll(/\D/g, '');

  // Case 1: phoneNumber1 has 011 prefix, phoneNumber2 doesn't
  if (digits1.startsWith('011') && !digits2.startsWith('011')) {
    return digits1.substring(3) === digits2;
  }

  // Case 2: phoneNumber2 has 011 prefix, phoneNumber1 doesn't
  if (digits2.startsWith('011') && !digits1.startsWith('011')) {
    return digits2.substring(3) === digits1;
  }

  return false;
};

/**
 * Formats phone number for callee-update requests:
 * - Leaves NANP numbers (US/Canada/Caribbean) in their original format
 * - Converts international numbers to '011' + digits (stripping + and formatting)
 *
 * @param phoneNumber - any string, e.g. "+442071234567", "442071234567", "2025550125"
 * @returns The formatted phone number
 */
export const formatPhoneNumberForCalleeUpdate = (
  phoneNumber: string
): string => {
  if (!phoneNumber) {
    return phoneNumber;
  }

  const raw = phoneNumber.trim();

  // Already in dial-out prefix form
  if (raw.startsWith('011')) {
    return raw;
  }

  // Handle E.164 format (+...)
  if (raw.startsWith('+')) {
    try {
      const pn = parsePhoneNumberFromString(raw);
      if (pn?.isValid()) {
        if (pn.countryCallingCode === '1') {
          // NANP E.164 — preserve original format
          return raw;
        }
        // non-NANP E.164 — strip '+' and prefix '011', clean formatting
        const cleanedDigits = raw.slice(1).replaceAll(/\D/g, '');
        return `011${cleanedDigits}`;
      }
    } catch {
      // treat as international - strip + and clean formatting
      const cleanedDigits = raw.slice(1).replaceAll(/\D/g, '');
      return `011${cleanedDigits}`;
    }
  }

  // Clean the digits for processing
  const digits = raw.replaceAll(/\D/g, '');

  // If the number is less than 7 digits, it's likely a short code,
  // internal extension, or malformed - return original format
  if (digits.length < 7) {
    return phoneNumber;
  }

  // Try international parsing first
  if (parseAsInternational(digits)) {
    return `011${digits}`;
  }

  // Try NANP parsing
  if (parseAsNANP(digits)) {
    return raw; // Return original format for NANP numbers
  }

  // Default: treat as international as some countries are not supported by libphonenumber-js
  return `011${digits}`;
};

/**
 * Checks if the callee connection string was converted during formatting
 * Returns true if the original and formatted versions differ (indicating conversion happened)
 */
export const wasCalleeConnectionStringConverted = (
  originalPhoneNumber: string,
  formattedPhoneNumber: string
): boolean => {
  if (!originalPhoneNumber || !formattedPhoneNumber) {
    return false;
  }
  return originalPhoneNumber !== formattedPhoneNumber;
};
